import React, { useMemo, useState } from 'react';
import Axios from 'axios';
import authConfig from '../../authConfig';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';

function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getDefaultDateRange() {
    const now = new Date();
    const endOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    const startDate = new Date(endOfPreviousMonth);
    startDate.setFullYear(startDate.getFullYear() - 1);

    return {
        from: formatDateForInput(startDate),
        to: formatDateForInput(endOfPreviousMonth)
    };
}

function csvEscape(value) {
    const text = value == null ? '' : String(value);
    if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

function formatMemberName(memberDetails) {
    if (!memberDetails) return '';
    const title = memberDetails.titleEn || '';
    const firstName = memberDetails.firstName || '';
    const surname = memberDetails.surname || '';
    return [title, firstName, surname].filter(Boolean).join(' ').trim();
}

export default function AllMemberStatistics() {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const isAuthenticated = useIsAuthenticated();

    const defaults = getDefaultDateRange();
    const [from, setFrom] = useState(defaults.from);
    const [to, setTo] = useState(defaults.to);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [committeeFilter, setCommitteeFilter] = useState('All Committees');
    const [valueDisplay, setValueDisplay] = useState('number');

    const memberIds = useMemo(() => Object.keys(data?.members || {}), [data]);
    const attendanceTypes = (data?.attendanceTypes || []).filter(type => type !== 'In Attendance');

    const formatAttendanceValue = (row, type) => {
        const total = row.totals[type] ?? 0;
        const percentage = row.percentages[type] ?? 0;
        return valueDisplay === 'percentage' ? `${percentage.toFixed(2)}%` : `${total}`;
    };

    const committeeOptions = useMemo(() => {
        const names = new Set(['All Committees']);
        memberIds.forEach(memberId => {
            const member = data?.members?.[memberId] || {};
            Object.keys(member.committees || {}).forEach(committeeId => {
                const committeeData = member.committees?.[committeeId] || {};
                const committeeName = committeeData.committee?.nameEn || committeeData.committee?.nameCy;
                if (committeeName) names.add(committeeName);
            });
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [data, memberIds]);

    const displayRows = useMemo(() => {
        const rows = [];
        const useAllCommittees = committeeFilter === 'All Committees';

        memberIds.forEach(memberId => {
            const member = data?.members?.[memberId] || {};
            const memberDetails = member.member || {};
            const memberName = formatMemberName(memberDetails) || 'Unknown Member';

            if (useAllCommittees)
            {
                rows.push({
                    titleEn: memberDetails.titleEn || '',
                    firstName: memberDetails.firstName || '',
                    surname: memberDetails.surname || '',
                    memberName,
                    committeeName: 'All Committees',
                    totals: member.totals || {},
                    percentages: member.percentages || {}
                });
            }
            else
            {
                const committees = member.committees || {};
                Object.keys(committees).forEach(committeeId => {
                    const committeeData = committees[committeeId] || {};
                    const name = committeeData.committee?.nameEn || committeeData.committee?.nameCy || 'Unknown Committee';
                    if (name === committeeFilter)
                    {
                        rows.push({
                            titleEn: memberDetails.titleEn || '',
                            firstName: memberDetails.firstName || '',
                            surname: memberDetails.surname || '',
                            memberName,
                            committeeName: name,
                            totals: committeeData.totals || {},
                            percentages: committeeData.percentages || {}
                        });
                    }
                });
            }
        });

        return rows;
    }, [data, memberIds, committeeFilter]);

    async function loadStatistics() {
        setLoading(true);
        setError('');

        try {
            if (!isAuthenticated || !account) {
                throw new Error('You must be signed in to load member statistics.');
            }

            const tokenResponse = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account
            });

            if (!tokenResponse?.accessToken) {
                throw new Error('Unable to acquire access token.');
            }

            const endpoint = authConfig.api?.urls?.getMemberAllMemberStatistics
                || `${authConfig.api?.urls?.getApiUrl}api/member/allmemberstatistics`;

            const response = await Axios.get(endpoint, {
                params: { from, to },
                headers: {
                    Authorization: `Bearer ${tokenResponse.accessToken}`
                },
                withCredentials: true
            });

            setData(response.data || { attendanceTypes: [], members: {} });
        } catch (ex) {
            setError(ex?.response?.data?.message || ex?.message || 'Failed to load statistics.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }

    function downloadCsv() {
        if (!displayRows.length) return;

        const rows = [];
        rows.push(['TitleEn', 'FirstName', 'Surname', 'MemberName', 'Committee', ...attendanceTypes]);

        displayRows.forEach(row => {
            rows.push([
                row.titleEn,
                row.firstName,
                row.surname,
                row.memberName,
                row.committeeName,
                ...attendanceTypes.map(type => formatAttendanceValue(row, type))
            ]);
        });

        const csv = rows.map(r => r.map(csvEscape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `all-member-statistics-${from}-to-${to}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    return (
        <div>
            <h5>All Member Attendance Statistics</h5>

            <Row className="g-2 align-items-end mb-3">
                <Col md={3}>
                    <Form.Label>Start date</Form.Label>
                    <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Col>
                <Col md={3}>
                    <Form.Label>End date</Form.Label>
                    <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </Col>
                <Col md={3}>
                    <Form.Label>Committee</Form.Label>
                    <Form.Select value={committeeFilter} onChange={(e) => setCommitteeFilter(e.target.value)} disabled={!data || loading}>
                        {committeeOptions.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={2}>
                    <Form.Label>Display</Form.Label>
                    <Form.Select value={valueDisplay} onChange={(e) => setValueDisplay(e.target.value)} disabled={!data || loading}>
                        <option value="number">Number</option>
                        <option value="percentage">Percentage</option>
                    </Form.Select>
                </Col>
                <Col md="auto">
                    <Button variant="primary" onClick={loadStatistics} disabled={loading || !from || !to}>
                        {loading ? 'Loading...' : 'Load statistics'}
                    </Button>
                </Col>
                <Col md="auto">
                    <Button variant="outline-success" onClick={downloadCsv} disabled={loading || !displayRows.length}>
                        Download CSV
                    </Button>
                </Col>
            </Row>

            {loading && (
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Spinner size="sm" animation="border" />
                    <span>Loading statistics...</span>
                </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && data && displayRows.length === 0 && (
                <Alert variant="info">No statistics found for the selected filters.</Alert>
            )}

            {!loading && displayRows.length > 0 && (
                <Table striped bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Member Name</th>
                            <th>Committee</th>
                            {attendanceTypes.map(type => (
                                <th key={`table-header-${type}`}>{type}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayRows.map((row, idx) => (
                            <tr key={`${row.memberName}-${row.committeeName}-${idx}`}>
                                <td>{row.memberName}</td>
                                <td>{row.committeeName}</td>
                                {attendanceTypes.map(type => (
                                    <td key={`${row.memberName}-${row.committeeName}-${type}-${idx}`}>
                                        {formatAttendanceValue(row, type)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
